import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getGroupById } from '../../api/groupsApi';
import { getOrganizationUsersByRole } from '../../api/organizationsApi';
import { getSubGroupsByGroupId } from '../../api/subGroupsApi';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { STATUS } from '../../enums/ENUMS';
import { RolesLowerCase } from '../../enums/ROLES';
import { GROUP, GROUP_WITH_SUBGROUP, SUBGROUP_LIST_ITEM, USER } from '../../enums/TYPES';
import { SortOptions, SortType } from '../../enums/SORT';

type TInitialState = {
  subGroups: SUBGROUP_LIST_ITEM[] | null;
  status: STATUS;
  allAnalysts: USER[];
  allViewers: USER[];
  currentGroup: GROUP | null;
  currentSubGroup: SUBGROUP_LIST_ITEM | null;
  sort: SortType;
  sortOption: SortOptions;
};

const initialState: TInitialState = {
  subGroups: null,
  status: STATUS.IDLE,
  allAnalysts: [],
  allViewers: [],
  currentGroup: null,
  currentSubGroup: null,
  sort: SortType.ASC,
  sortOption: SortOptions.NAME,
};

export const getSubGroupsByGroupIdThunk = createAsyncThunk(
  API_ROUTES.SUBGROUPS,
  async (data: { groupId: string; sort: SortType; sortOption: SortOptions }) => {
    const group = await getGroupById(data.groupId);
    const subgroups = await getSubGroupsByGroupId(data.groupId, data.sort, data.sortOption);
    return {
      group: group.data,
      subgroups: subgroups.data,
    };
  },
);

export const getUsersByOrganization = createAsyncThunk(API_ROUTES.ORGANIZATION_USERS, async () => {
  const analysts = await getOrganizationUsersByRole(RolesLowerCase.ANALYST);
  const viewers = await getOrganizationUsersByRole(RolesLowerCase.VIEWER);
  return {
    analysts: analysts.data.documents,
    viewers: viewers.data.documents,
  };
});

export const subGroupsSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    saveGroupsAndSubgroups: (state, action: PayloadAction<GROUP_WITH_SUBGROUP>) => {
      state.subGroups = action.payload.subgroups;
      state.currentGroup = action.payload;
    },
    updateSortOption: (state, action) => {
      state.sortOption = action.payload;
    },
    updateSortType: (state, action) => {
      state.sort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubGroupsByGroupIdThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(
        getSubGroupsByGroupIdThunk.fulfilled,
        (state, action: PayloadAction<{ group: GROUP_WITH_SUBGROUP; subgroups: SUBGROUP_LIST_ITEM[] }>) => {
          state.status = STATUS.IDLE;
          state.subGroups = action.payload.subgroups ?? [];
          state.currentGroup = action.payload.group;
        },
      )
      .addCase(getSubGroupsByGroupIdThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getUsersByOrganization.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getUsersByOrganization.fulfilled, (state, action: PayloadAction<{ analysts: USER[]; viewers: USER[] }>) => {
        state.status = STATUS.IDLE;
        state.allAnalysts = action.payload.analysts;
        state.allViewers = action.payload.viewers;
      })
      .addCase(getUsersByOrganization.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
  },
});

export const { saveGroupsAndSubgroups, updateSortOption, updateSortType } = subGroupsSlice.actions;

export default subGroupsSlice.reducer;
